import { z } from 'zod'

const hexColorRegex = /^#([0-9A-Fa-f]{3}){1,2}$/
const optionalNumMin0 = z.coerce.number().min(0).optional()
const legalLink = z
  .string()
  .optional()
  .refine((v) => !v?.trim() || /^https?:\/\/\S+$/i.test(v.trim()), 'Enter a full link starting with https://')

const attendanceSettingsSchema = z
  .object({
    minimumHoursForPresent: optionalNumMin0,
    lateGracePeriodMinutes: optionalNumMin0,
    autoDeductLateMinutes: z.boolean().optional(),
  })
  .optional()

const breakSettingsSchema = z
  .object({
    defaultPaidBreakMinutes: optionalNumMin0,
    defaultUnpaidBreakMinutes: optionalNumMin0,
    autoDeductExcessBreak: z.boolean().optional(),
  })
  .optional()

const agentSettingsSchema = z
  .object({
    screenshotEnabled: z.boolean().optional(),
    screenshotIntervalMinutes: optionalNumMin0,
    screenshotMode: z.enum(['random', 'fixed']).optional(),
    inactivityThresholdMinutes: optionalNumMin0,
    inactivityAlertEnabled: z.boolean().optional(),
  })
  .optional()

const shiftAlertSettingsSchema = z
  .object({
    preShiftAlertMinutes: optionalNumMin0,
    reminderIntervalMinutes: optionalNumMin0,
    maxMissedReminders: optionalNumMin0,
    escalateToAdmin: z.boolean().optional(),
    suppressForLeave: z.boolean().optional(),
  })
  .optional()

const payrollSettingsSchema = z
  .object({
    payrollCycleType: z.enum(['monthly', 'biweekly', 'weekly']).optional(),
    requireApproval: z.boolean().optional(),
    lockAfterApproval: z.boolean().optional(),
  })
  .optional()

const leaveSettingsSchema = z
  .object({
    requireHRApproval: z.boolean().optional(),
    requireAdminFinalApproval: z.boolean().optional(),
    allowNegativeBalance: z.boolean().optional(),
  })
  .optional()

const notificationSettingsSchema = z
  .object({
    emailEnabled: z.boolean().optional(),
    inAppEnabled: z.boolean().optional(),
  })
  .optional()

const chatSettingsSchema = z
  .object({
    directMessagesEnabled: z.boolean().optional(),
    directMessageScope: z.enum(['within_scope', 'all_organization_members']).optional(),
    allowAllRolesToCreateChannels: z.boolean().optional(),
    allowVoiceNotes: z.boolean().optional(),
    allowManagementToStaffDirectMessages: z.boolean().optional(),
    allowStaffToStaffDirectMessages: z.boolean().optional(),
    allowStaffToManagementDirectMessages: z.boolean().optional(),
  })
  .optional()

export const organizationFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().optional(),
  registrationNumber: z.string().optional(),
  taxId: z.string().optional(),
  industry: z.string().optional(),
  contactEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  contactPhone: z.string().optional(),
  website: z.string().url('Enter a valid URL').optional().or(z.literal('')),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  primaryColor: z
    .string()
    .optional()
    .refine((v) => !v || hexColorRegex.test(v), 'Enter a valid hex color (e.g. #1E40AF)'),
  footerText: z.string().max(200, 'Use 200 characters or fewer').optional(),
  termsUrl: legalLink,
  privacyUrl: legalLink,
  status: z.enum(['active', 'suspended', 'deactivated']),
  defaultCurrency: z.string().optional(),
  supportedCurrencies: z.array(z.string()).optional(),
  timezone: z.string().optional(),
  dateFormat: z.string().optional(),
  companyCodePrefix: z
    .string()
    .regex(/^[A-Za-z0-9]{2,10}$/, 'Use 2-10 letters or numbers')
    .optional()
    .or(z.literal('')),
  workWeek: z.array(z.string()).optional(),
  attendanceSettings: attendanceSettingsSchema,
  breakSettings: breakSettingsSchema,
  agentSettings: agentSettingsSchema,
  shiftAlertSettings: shiftAlertSettingsSchema,
  payrollSettings: payrollSettingsSchema,
  leaveSettings: leaveSettingsSchema,
  notificationSettings: notificationSettingsSchema,
  chatSettings: chatSettingsSchema,
})

export type OrganizationFormValues = z.infer<typeof organizationFormSchema>

export type TabId = 'overview' | 'address' | 'branding' | 'settings'
